'use client';

/**
 * Pure-SVG donut chart — no interactivity, only its accessible name
 * comes from the catalogue. Used by profile + my-learning screens to
 * show completed / in-progress / enrolled splits.
 *
 * <p>Math: each segment is drawn via {@code stroke-dasharray} on a
 * circle whose circumference equals the sum of all values. The first
 * segment starts at 12 o'clock via a {@code rotate(-90)}.
 */
import { useTranslation } from 'react-i18next';

export interface DonutSegment {
  label: string;
  value: number;
  color: string;
}

export interface DonutProps {
  segments: DonutSegment[];
  size?: number;
  thickness?: number;
  centerLabel?: string;
  centerValue?: string;
}

export function Donut({
  segments,
  size = 160,
  thickness = 22,
  centerLabel,
  centerValue,
}: DonutProps) {
  const { t } = useTranslation('site-builder-blocks');
  const total = segments.reduce((a, b) => a + b.value, 0);
  const radius = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const C = 2 * Math.PI * radius;

  let offset = 0;

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 24 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
        <title>{t('donut.chartTitle')}</title>
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="var(--color-neutral-100, #f4f4f5)"
          strokeWidth={thickness}
        />
        {total > 0 &&
          segments.map((s, i) => {
            const dash = (s.value / total) * C;
            const seg = (
              <circle
                key={i}
                cx={cx}
                cy={cy}
                r={radius}
                fill="none"
                stroke={s.color}
                strokeWidth={thickness}
                strokeDasharray={`${dash} ${C - dash}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${cx} ${cy})`}
              />
            );
            offset += dash;
            return seg;
          })}
        {centerValue && (
          <text
            x={cx}
            y={cy - 4}
            textAnchor="middle"
            style={{
              fontSize: 'var(--font-size-xl, 22px)',
              fontWeight: 600,
              fill: 'var(--color-text-primary, #18181b)',
            }}
          >
            {centerValue}
          </text>
        )}
        {centerLabel && (
          <text
            x={cx}
            y={cy + 16}
            textAnchor="middle"
            style={{ fontSize: '11px', fill: 'var(--color-text-secondary, #71717a)' }}
          >
            {centerLabel}
          </text>
        )}
      </svg>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {segments.map((s, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '13px' }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: s.color,
                flexShrink: 0,
              }}
            />
            <span style={{ color: 'var(--color-text-primary, #18181b)' }}>{s.label}</span>
            <span
              style={{
                fontFamily: 'var(--font-family-mono, ui-monospace, monospace)',
                color: 'var(--color-text-secondary, #71717a)',
              }}
            >
              {s.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
